<template>
  <div>
    <!-- Hero Section -->
    <section class="relative overflow-hidden py-20 sm:py-32 min-h-[90vh] flex items-center">
      <!-- Modern Animated Background -->
      <div class="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-purple-50"></div>
      
      <!-- Animated gradient overlay -->
      <div class="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-purple-500/5 animate-gradient"></div>
      
      <!-- Animated decorative elements -->
      <div class="absolute inset-0 overflow-hidden">
        <!-- Animated grid pattern -->
        <div class="absolute inset-0 opacity-[0.03] animate-grid" style="background-image: linear-gradient(to right, #3b82f6 1px, transparent 1px), linear-gradient(to bottom, #3b82f6 1px, transparent 1px); background-size: 50px 50px;"></div>
        
        <!-- Floating orbs -->
        <div class="absolute top-20 right-1/4 w-72 h-72 bg-blue-400/20 rounded-full blur-3xl animate-float"></div>
        <div class="absolute bottom-20 left-1/4 w-96 h-96 bg-purple-400/15 rounded-full blur-3xl animate-float-delayed"></div>
      </div>

      <div class="container mx-auto px-4 relative z-10">
        <div class="max-w-5xl mx-auto text-center">
          <!-- Special Badge -->
          <div class="inline-flex items-center rounded-full bg-gradient-to-r from-green-500 to-teal-500 text-white px-5 py-2 text-sm font-semibold shadow-lg mb-6 animate-pulse-slow">
            <span class="flex h-2 w-2 rounded-full bg-white mr-2 animate-ping"></span>
            🎁 {{ $t('websiteSolution.specialOffer') }}
          </div>
          
          <!-- Main Headline with Gradient -->
          <h1 class="text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.1] mb-6">
            <span class="bg-gradient-to-r from-gray-900 via-blue-600 to-purple-600 bg-clip-text text-transparent">
              {{ $t('sitiWebAvvocati.hero.title') }}
            </span>
          </h1>
          
          <!-- Description -->
          <p class="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed mb-8 font-medium">
            {{ $t('sitiWebAvvocati.hero.description') }}
          </p>

          <!-- Key Benefits Pills -->
          <div class="mt-8 flex flex-wrap justify-center gap-3 mb-8">
            <div class="px-5 py-2.5 bg-white/90 backdrop-blur-sm rounded-full border-2 border-blue-200 shadow-md hover:shadow-lg text-sm font-semibold text-gray-700 transition-all duration-300 transform hover:scale-105">
              <Check class="inline h-4 w-4 text-green-500 mr-1" />
              {{ $t('sitiWebAvvocati.hero.benefit1') }}
            </div>
            <div class="px-5 py-2.5 bg-white/90 backdrop-blur-sm rounded-full border-2 border-blue-200 shadow-md hover:shadow-lg text-sm font-semibold text-gray-700 transition-all duration-300 transform hover:scale-105">
              <Check class="inline h-4 w-4 text-green-500 mr-1" />
              {{ $t('sitiWebAvvocati.hero.benefit2') }}
            </div>
            <div class="px-5 py-2.5 bg-white/90 backdrop-blur-sm rounded-full border-2 border-blue-200 shadow-md hover:shadow-lg text-sm font-semibold text-gray-700 transition-all duration-300 transform hover:scale-105">
              <Check class="inline h-4 w-4 text-green-500 mr-1" />
              {{ $t('sitiWebAvvocati.hero.benefit3') }}
            </div>
          </div>
          
          <!-- Enhanced CTAs -->
          <div class="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
            <router-link 
              to="/contact" 
              class="group relative h-16 px-10 bg-gradient-to-r from-blue-600 via-blue-500 to-purple-600 hover:from-blue-700 hover:via-blue-600 hover:to-purple-700 text-white rounded-2xl flex items-center justify-center font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-2xl hover:shadow-blue-500/50 animate-bounce-subtle"
            >
              <span class="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-400 to-purple-400 opacity-0 group-hover:opacity-100 blur-xl transition-opacity"></span>
              <MessageSquare class="mr-3 h-6 w-6 relative z-10" />
              <span class="relative z-10">{{ $t('sitiWebAvvocati.hero.cta') }}</span>
            </router-link>
            
            <router-link 
              to="/preventivo-sito-web" 
              class="h-16 px-8 border-3 border-blue-500 hover:border-blue-600 text-blue-600 hover:text-blue-700 rounded-2xl flex items-center justify-center font-bold text-lg transition-all duration-300 transform hover:scale-105 bg-white shadow-lg hover:shadow-xl"
            >
              <Calculator class="mr-2 h-5 w-5" />
              {{ $t('sitiWebAvvocati.hero.ctaSecondary') }}
            </router-link>
          </div>

          <!-- Social Proof -->
          <div class="flex flex-wrap justify-center items-center gap-6 text-sm text-gray-600">
            <div class="flex items-center bg-white/80 backdrop-blur-sm rounded-full px-4 py-2 border border-gray-200 shadow-sm">
              <Star class="h-5 w-5 text-yellow-400 mr-1 fill-yellow-400" />
              <span class="font-bold">4.9/5</span>
              <span class="ml-1">Clienti Soddisfatti</span>
            </div>
            <div class="flex items-center bg-white/80 backdrop-blur-sm rounded-full px-4 py-2 border border-gray-200 shadow-sm">
              <Award class="h-5 w-5 text-blue-500 mr-1" />
              <span class="font-semibold">Esperti in Legal Tech</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <PartnersSection />

    <!-- Problem/Solution Section -->
    <section class="relative py-20 bg-gradient-to-br from-white via-blue-50/30 to-white overflow-hidden">
      <!-- Background decoration -->
      <div class="absolute inset-0">
        <div class="absolute top-0 right-0 w-64 h-64 bg-blue-400/10 rounded-full blur-3xl"></div>
        <div class="absolute bottom-0 left-0 w-64 h-64 bg-purple-400/10 rounded-full blur-3xl"></div>
      </div>
      <div class="container mx-auto px-4 relative z-10">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold mb-6">
            <span class="bg-gradient-to-r from-blue-600 via-blue-500 to-purple-600 bg-clip-text text-transparent">
              {{ $t('sitiWebAvvocati.problemSolution.title') }}
            </span>
          </h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            {{ $t('sitiWebAvvocati.problemSolution.subtitle') }}
          </p>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <!-- Problems -->
          <div>
            <h3 class="text-2xl font-bold mb-6 text-gray-800">{{ $t('sitiWebAvvocati.problemSolution.problems.title') }}</h3>
            <div class="space-y-4">
              <div class="group flex items-start space-x-3 bg-white/80 backdrop-blur-sm rounded-xl p-5 border-2 border-red-200 hover:border-red-300 shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-[1.02]">
                <div class="w-8 h-8 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1 shadow-lg">
                  <span class="text-white text-sm font-bold">×</span>
                </div>
                <div>
                  <p class="text-gray-800 font-bold">{{ $t('sitiWebAvvocati.problemSolution.problems.outdated.title') }}</p>
                  <p class="text-gray-600 text-sm leading-relaxed">{{ $t('sitiWebAvvocati.problemSolution.problems.outdated.description') }}</p>
                </div>
              </div>
              <div class="group flex items-start space-x-3 bg-white/80 backdrop-blur-sm rounded-xl p-5 border-2 border-red-200 hover:border-red-300 shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-[1.02]">
                <div class="w-8 h-8 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1 shadow-lg">
                  <span class="text-white text-sm font-bold">×</span>
                </div>
                <div>
                  <p class="text-gray-800 font-bold">{{ $t('sitiWebAvvocati.problemSolution.problems.mobile.title') }}</p>
                  <p class="text-gray-600 text-sm leading-relaxed">{{ $t('sitiWebAvvocati.problemSolution.problems.mobile.description') }}</p>
                </div>
              </div>
              <div class="group flex items-start space-x-3 bg-white/80 backdrop-blur-sm rounded-xl p-5 border-2 border-red-200 hover:border-red-300 shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-[1.02]">
                <div class="w-8 h-8 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1 shadow-lg">
                  <span class="text-white text-sm font-bold">×</span>
                </div>
                <div>
                  <p class="text-gray-800 font-bold">{{ $t('sitiWebAvvocati.problemSolution.problems.seo.title') }}</p>
                  <p class="text-gray-600 text-sm leading-relaxed">{{ $t('sitiWebAvvocati.problemSolution.problems.seo.description') }}</p>
                </div>
              </div>
              <div class="group flex items-start space-x-3 bg-white/80 backdrop-blur-sm rounded-xl p-5 border-2 border-red-200 hover:border-red-300 shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-[1.02]">
                <div class="w-8 h-8 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1 shadow-lg">
                  <span class="text-white text-sm font-bold">×</span>
                </div>
                <div>
                  <p class="text-gray-800 font-bold">{{ $t('sitiWebAvvocati.problemSolution.problems.trust.title') }}</p>
                  <p class="text-gray-600 text-sm leading-relaxed">{{ $t('sitiWebAvvocati.problemSolution.problems.trust.description') }}</p>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Solutions -->
          <div>
            <h3 class="text-2xl font-bold mb-6 text-gray-800">{{ $t('sitiWebAvvocati.problemSolution.solutions.title') }}</h3>
            <div class="space-y-4">
              <div class="group flex items-start space-x-3 bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-xl p-5 border-2 border-blue-200 hover:border-blue-300 shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-[1.02]">
                <div class="w-8 h-8 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1 shadow-lg">
                  <Check class="h-5 w-5 text-white" />
                </div>
                <div>
                  <p class="text-gray-800 font-bold">{{ $t('sitiWebAvvocati.problemSolution.solutions.modern.title') }}</p>
                  <p class="text-gray-600 text-sm leading-relaxed">{{ $t('sitiWebAvvocati.problemSolution.solutions.modern.description') }}</p>
                </div>
              </div>
              <div class="group flex items-start space-x-3 bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-xl p-5 border-2 border-blue-200 hover:border-blue-300 shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-[1.02]">
                <div class="w-8 h-8 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1 shadow-lg">
                  <Check class="h-5 w-5 text-white" />
                </div>
                <div>
                  <p class="text-gray-800 font-bold">{{ $t('sitiWebAvvocati.problemSolution.solutions.responsive.title') }}</p>
                  <p class="text-gray-600 text-sm leading-relaxed">{{ $t('sitiWebAvvocati.problemSolution.solutions.responsive.description') }}</p>
                </div>
              </div>
              <div class="group flex items-start space-x-3 bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-xl p-5 border-2 border-blue-200 hover:border-blue-300 shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-[1.02]">
                <div class="w-8 h-8 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1 shadow-lg">
                  <Check class="h-5 w-5 text-white" />
                </div>
                <div>
                  <p class="text-gray-800 font-bold">{{ $t('sitiWebAvvocati.problemSolution.solutions.optimized.title') }}</p>
                  <p class="text-gray-600 text-sm leading-relaxed">{{ $t('sitiWebAvvocati.problemSolution.solutions.optimized.description') }}</p>
                </div>
              </div>
              <div class="group flex items-start space-x-3 bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-xl p-5 border-2 border-blue-200 hover:border-blue-300 shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-[1.02]">
                <div class="w-8 h-8 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1 shadow-lg">
                  <Check class="h-5 w-5 text-white" />
                </div>
                <div>
                  <p class="text-gray-800 font-bold">{{ $t('sitiWebAvvocati.problemSolution.solutions.professional.title') }}</p>
                  <p class="text-gray-600 text-sm leading-relaxed">{{ $t('sitiWebAvvocati.problemSolution.solutions.professional.description') }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section class="relative py-20 bg-gradient-to-br from-gray-50 via-white to-blue-50/30 overflow-hidden">
      <!-- Background decoration -->
      <div class="absolute inset-0">
        <div class="absolute top-0 left-0 w-64 h-64 bg-blue-400/10 rounded-full blur-3xl animate-float"></div>
        <div class="absolute bottom-0 right-0 w-64 h-64 bg-purple-400/10 rounded-full blur-3xl animate-float-delayed"></div>
      </div>
      
      <div class="container mx-auto px-4 relative z-10">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold mb-6">
            <span class="bg-gradient-to-r from-blue-600 via-blue-500 to-purple-600 bg-clip-text text-transparent">
              {{ $t('sitiWebAvvocati.features.title') }}
            </span>
          </h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            {{ $t('sitiWebAvvocati.features.subtitle') }}
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div v-for="feature in features" :key="feature.title" 
               class="group relative bg-white/80 backdrop-blur-sm rounded-2xl p-8 border-2 border-gray-200 hover:border-blue-300 shadow-md hover:shadow-xl transition-all duration-300 transform hover:scale-105 hover:-translate-y-2">
            <div class="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl"></div>
            <div class="relative z-10">
              <div class="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mb-6 shadow-lg group-hover:shadow-blue-500/50 transition-all duration-300 group-hover:scale-110">
                <component :is="feature.icon" class="h-7 w-7 text-white" />
              </div>
              <h3 class="text-xl font-bold mb-4 text-gray-800">{{ feature.title }}</h3>
              <p class="text-gray-600 leading-relaxed">{{ feature.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Why Choose Us Section -->
    <section class="relative py-20 bg-gradient-to-br from-white via-blue-50/30 to-white overflow-hidden">
      <!-- Background decoration -->
      <div class="absolute inset-0">
        <div class="absolute top-0 right-0 w-64 h-64 bg-blue-400/10 rounded-full blur-3xl"></div>
        <div class="absolute bottom-0 left-0 w-64 h-64 bg-purple-400/10 rounded-full blur-3xl"></div>
      </div>
      
      <div class="container mx-auto px-4 relative z-10">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold mb-6">
            <span class="bg-gradient-to-r from-blue-600 via-blue-500 to-purple-600 bg-clip-text text-transparent">
              {{ $t('sitiWebAvvocati.whyChoose.title') }}
            </span>
          </h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            {{ $t('sitiWebAvvocati.whyChoose.subtitle') }}
          </p>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div class="space-y-6">
            <div v-for="reason in whyChooseReasons" :key="reason.title" 
                 class="group flex items-start space-x-4 bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-gray-200 hover:border-blue-300 shadow-md hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02]">
              <div class="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg group-hover:shadow-blue-500/50 transition-all duration-300 group-hover:scale-110">
                <component :is="reason.icon" class="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 class="text-lg font-bold mb-2 text-gray-800">{{ reason.title }}</h3>
                <p class="text-gray-600 leading-relaxed">{{ reason.description }}</p>
              </div>
            </div>
          </div>
          
          <div class="relative">
            <div class="group bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-2xl border-2 border-gray-200 hover:border-blue-300 transition-all duration-300 transform hover:scale-105">
              <div class="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl"></div>
              <div class="text-center relative z-10">
                <div class="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <TrendingUp class="h-10 w-10 text-white" />
                </div>
                <h3 class="text-2xl font-bold mb-6 text-gray-800">{{ $t('sitiWebAvvocati.whyChoose.stats.title') }}</h3>
                <div class="grid grid-cols-2 gap-6">
                  <div class="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-xl p-4 border border-blue-200">
                    <div class="text-4xl font-extrabold text-blue-600 mb-2">{{ $t('sitiWebAvvocati.whyChoose.stats.clientsValue') }}</div>
                    <div class="text-sm text-gray-700 font-semibold">{{ $t('sitiWebAvvocati.whyChoose.stats.clients') }}</div>
                  </div>
                  <div class="bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-xl p-4 border border-purple-200">
                    <div class="text-4xl font-extrabold text-purple-600 mb-2">{{ $t('sitiWebAvvocati.whyChoose.stats.growthValue') }}</div>
                    <div class="text-sm text-gray-700 font-semibold">{{ $t('sitiWebAvvocati.whyChoose.stats.growth') }}</div>
                  </div>
                  <div class="bg-gradient-to-br from-green-50 to-green-100/50 rounded-xl p-4 border border-green-200">
                    <div class="text-4xl font-extrabold text-green-600 mb-2">{{ $t('sitiWebAvvocati.whyChoose.stats.speedValue') }}</div>
                    <div class="text-sm text-gray-700 font-semibold">{{ $t('sitiWebAvvocati.whyChoose.stats.speed') }}</div>
                  </div>
                  <div class="bg-gradient-to-br from-orange-50 to-orange-100/50 rounded-xl p-4 border border-orange-200">
                    <div class="text-4xl font-extrabold text-orange-600 mb-2">{{ $t('sitiWebAvvocati.whyChoose.stats.supportValue') }}</div>
                    <div class="text-sm text-gray-700 font-semibold">{{ $t('sitiWebAvvocati.whyChoose.stats.support') }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- How It Works Section -->
    <section class="relative py-20 bg-gradient-to-br from-gray-50 via-white to-blue-50/30 overflow-hidden">
      <!-- Background decoration -->
      <div class="absolute inset-0">
        <div class="absolute top-0 left-0 w-64 h-64 bg-blue-400/10 rounded-full blur-3xl animate-float"></div>
        <div class="absolute bottom-0 right-0 w-64 h-64 bg-purple-400/10 rounded-full blur-3xl animate-float-delayed"></div>
      </div>
      
      <div class="container mx-auto px-4 relative z-10">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold mb-6">
            <span class="bg-gradient-to-r from-blue-600 via-blue-500 to-purple-600 bg-clip-text text-transparent">
              {{ $t('sitiWebAvvocati.howItWorks.title') }}
            </span>
          </h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            {{ $t('sitiWebAvvocati.howItWorks.subtitle') }}
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div v-for="(step, index) in howItWorksSteps" :key="index" 
               class="group relative bg-white/80 backdrop-blur-sm rounded-2xl p-8 border-2 border-gray-200 hover:border-blue-300 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 hover:-translate-y-2 text-center">
            <div class="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl"></div>
            <div class="relative z-10">
              <div class="w-24 h-24 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6 text-white font-bold text-3xl shadow-xl group-hover:shadow-blue-500/50 transition-all duration-300 group-hover:scale-110 group-hover:rotate-3">
                {{ index + 1 }}
              </div>
              <h3 class="text-xl font-bold mb-4 text-gray-800">{{ step.title }}</h3>
              <p class="text-gray-600 leading-relaxed">{{ step.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Use Cases Section -->
    <section class="py-20 bg-white">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold mb-6">
            <span class="bg-gradient-to-r from-blue-600 to-blue-500 bg-clip-text text-transparent">
              {{ $t('sitiWebAvvocati.useCases.title') }}
            </span>
          </h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            {{ $t('sitiWebAvvocati.useCases.subtitle') }}
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div v-for="useCase in useCases" :key="useCase.title" class="bg-gray-50 rounded-xl p-6 hover:bg-gray-100 transition-colors text-center">
            <div class="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <component :is="useCase.icon" class="h-8 w-8 text-blue-600" />
            </div>
            <h3 class="text-lg font-semibold mb-2 text-gray-800">{{ useCase.title }}</h3>
            <p class="text-sm text-gray-600">{{ useCase.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Technology & Features Section -->
    <section class="py-20 bg-gray-50">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold mb-6">
            <span class="bg-gradient-to-r from-blue-600 to-blue-500 bg-clip-text text-transparent">
              {{ $t('sitiWebAvvocati.technology.title') }}
            </span>
          </h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            {{ $t('sitiWebAvvocati.technology.subtitle') }}
          </p>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <h3 class="text-2xl font-bold mb-6 text-gray-800">{{ $t('sitiWebAvvocati.technology.modern.title') }}</h3>
            <div class="space-y-4">
              <div class="flex items-start space-x-3">
                <div class="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <Check class="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <p class="text-gray-700 font-medium">{{ $t('sitiWebAvvocati.technology.modern.responsive.title') }}</p>
                  <p class="text-gray-600 text-sm">{{ $t('sitiWebAvvocati.technology.modern.responsive.description') }}</p>
                </div>
              </div>
              <div class="flex items-start space-x-3">
                <div class="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <Check class="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <p class="text-gray-700 font-medium">{{ $t('sitiWebAvvocati.technology.modern.performance.title') }}</p>
                  <p class="text-gray-600 text-sm">{{ $t('sitiWebAvvocati.technology.modern.performance.description') }}</p>
                </div>
              </div>
              <div class="flex items-start space-x-3">
                <div class="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <Check class="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <p class="text-gray-700 font-medium">{{ $t('sitiWebAvvocati.technology.modern.security.title') }}</p>
                  <p class="text-gray-600 text-sm">{{ $t('sitiWebAvvocati.technology.modern.security.description') }}</p>
                </div>
              </div>
            </div>
          </div>
          
          <div>
            <h3 class="text-2xl font-bold mb-6 text-gray-800">{{ $t('sitiWebAvvocati.technology.legal.title') }}</h3>
            <div class="grid grid-cols-2 gap-4">
              <div v-for="feature in legalFeatures" :key="feature.name" class="bg-white rounded-lg p-4 text-center border border-gray-200">
                <component :is="feature.icon" class="h-8 w-8 text-blue-600 mx-auto mb-2" />
                <p class="text-sm font-medium text-gray-800">{{ feature.name }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Benefits Section -->
    <section class="py-20 bg-white">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold mb-6">
            <span class="bg-gradient-to-r from-blue-600 to-blue-500 bg-clip-text text-transparent">
              {{ $t('sitiWebAvvocati.benefits.title') }}
            </span>
          </h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            {{ $t('sitiWebAvvocati.benefits.subtitle') }}
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div v-for="benefit in benefits" :key="benefit.title" class="bg-gradient-to-br from-blue-50 to-white rounded-xl p-8 shadow-sm border border-gray-200">
            <div class="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
              <component :is="benefit.icon" class="h-6 w-6 text-blue-600" />
            </div>
            <h3 class="text-xl font-semibold mb-4 text-gray-800">{{ benefit.title }}</h3>
            <p class="text-gray-600">{{ benefit.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ Section -->
    <section class="relative py-20 bg-gradient-to-br from-gray-50 via-white to-blue-50/30 overflow-hidden">
      <!-- Background decoration -->
      <div class="absolute inset-0">
        <div class="absolute top-0 right-0 w-64 h-64 bg-blue-400/10 rounded-full blur-3xl"></div>
        <div class="absolute bottom-0 left-0 w-64 h-64 bg-purple-400/10 rounded-full blur-3xl"></div>
      </div>
      
      <div class="container mx-auto px-4 relative z-10">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold mb-6">
            <span class="bg-gradient-to-r from-blue-600 via-blue-500 to-purple-600 bg-clip-text text-transparent">
              {{ $t('sitiWebAvvocati.faq.title') }}
            </span>
          </h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            {{ $t('sitiWebAvvocati.faq.subtitle') }}
          </p>
        </div>

        <div class="max-w-4xl mx-auto space-y-6">
          <div v-for="(faq, index) in faqs" :key="index" 
               class="group bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-md border border-gray-200 hover:shadow-lg transition-all duration-300 transform hover:scale-[1.02]">
            <div class="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl"></div>
            <h3 class="text-lg font-bold mb-3 text-gray-800 relative z-10">{{ faq.question }}</h3>
            <p class="text-gray-600 leading-relaxed relative z-10">{{ faq.answer }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="relative py-20 bg-gradient-to-br from-blue-600 via-blue-700 to-purple-600 overflow-hidden">
      <!-- Background decoration -->
      <div class="absolute inset-0">
        <div class="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
        <div class="absolute bottom-0 left-0 w-96 h-96 bg-purple-400/10 rounded-full blur-3xl"></div>
      </div>
      
      <div class="container mx-auto px-4 text-center relative z-10">
        <div class="max-w-5xl mx-auto">
          <!-- Special Offer Badge -->
          <div class="inline-flex items-center rounded-full bg-gradient-to-r from-green-400 to-emerald-500 text-white px-4 py-1.5 text-sm font-bold mb-6 animate-pulse">
            <span class="flex h-2 w-2 rounded-full bg-white mr-2 animate-ping"></span>
            🎁 {{ $t('websiteSolution.specialOffer') }}
          </div>
          
          <h2 class="text-4xl md:text-5xl font-extrabold text-white mb-4">
            {{ $t('sitiWebAvvocati.cta.title') }}
          </h2>
          <p class="text-xl md:text-2xl mb-4 text-white/90 font-semibold max-w-3xl mx-auto">
            {{ $t('sitiWebAvvocati.cta.description') }}
          </p>
          
          <!-- Dual CTA Buttons -->
          <div class="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
            <router-link 
              to="/contact" 
              class="group relative h-16 px-10 bg-white text-blue-600 rounded-2xl inline-flex items-center justify-center font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-2xl hover:shadow-white/50"
            >
              <span class="absolute inset-0 rounded-2xl bg-white/80 opacity-0 group-hover:opacity-100 blur-xl transition-opacity"></span>
              <MessageSquare class="mr-3 h-6 w-6 relative z-10" />
              <span class="relative z-10">{{ $t('sitiWebAvvocati.cta.button') }}</span>
            </router-link>
            
            <router-link 
              to="/preventivo-sito-web" 
              class="h-16 px-8 bg-white/10 backdrop-blur-sm border-2 border-white/50 text-white rounded-2xl inline-flex items-center justify-center font-semibold text-lg transition-all duration-300 transform hover:scale-105 hover:bg-white/20 hover:border-white"
            >
              <Calculator class="mr-2 h-5 w-5" />
              {{ $t('sitiWebAvvocati.cta.buttonSecondary') }}
            </router-link>
          </div>
          
          <!-- Trust Badges -->
          <div class="flex flex-wrap justify-center gap-6 text-sm text-white/90">
            <div class="flex items-center">
              <Check class="h-5 w-5 text-green-300 mr-2" />
              {{ $t('websiteSolution.trust.freeChatbot') }}
            </div>
            <div class="flex items-center">
              <Check class="h-5 w-5 text-green-300 mr-2" />
              {{ $t('websiteSolution.trust.seo') }}
            </div>
            <div class="flex items-center">
              <Check class="h-5 w-5 text-green-300 mr-2" />
              {{ $t('websiteSolution.trust.support') }}
            </div>
          </div>
        </div>
      </div>
    </section>
    
    <FloatingCta />
  </div>
</template>

<script setup>
import { 
  MessageSquare, 
  Calculator,
  Check, 
  Star,
  Users,
  Award,
  Shield, 
  BarChart3, 
  TrendingUp,
  Clock,
  Zap,
  Target,
  FileText,
  Calendar,
  Phone,
  Briefcase,
  Scale,
  Gavel,
  BookOpen,
  Search,
  Smartphone,
  Lock
} from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'
import PartnersSection from '../components/PartnersSection.vue'
import FloatingCta from '../components/FloatingCta.vue'

const { t } = useI18n()

const features = [
  {
    icon: Search,
    title: t('sitiWebAvvocati.features.seo.title'),
    description: t('sitiWebAvvocati.features.seo.description')
  },
  {
    icon: Smartphone,
    title: t('sitiWebAvvocati.features.responsive.title'),
    description: t('sitiWebAvvocati.features.responsive.description')
  },
  {
    icon: Zap,
    title: t('sitiWebAvvocati.features.performance.title'),
    description: t('sitiWebAvvocati.features.performance.description')
  },
  {
    icon: Lock,
    title: t('sitiWebAvvocati.features.security.title'),
    description: t('sitiWebAvvocati.features.security.description')
  },
  {
    icon: Calendar,
    title: t('sitiWebAvvocati.features.booking.title'),
    description: t('sitiWebAvvocati.features.booking.description')
  },
  {
    icon: FileText,
    title: t('sitiWebAvvocati.features.cms.title'),
    description: t('sitiWebAvvocati.features.cms.description')
  }
]

const whyChooseReasons = [
  {
    icon: Briefcase,
    title: t('sitiWebAvvocati.whyChoose.reason1.title'),
    description: t('sitiWebAvvocati.whyChoose.reason1.description')
  },
  {
    icon: Shield,
    title: t('sitiWebAvvocati.whyChoose.reason2.title'),
    description: t('sitiWebAvvocati.whyChoose.reason2.description')
  },
  {
    icon: Target,
    title: t('sitiWebAvvocati.whyChoose.reason3.title'),
    description: t('sitiWebAvvocati.whyChoose.reason3.description')
  },
  {
    icon: Clock,
    title: t('sitiWebAvvocati.whyChoose.reason4.title'),
    description: t('sitiWebAvvocati.whyChoose.reason4.description')
  }
]

const howItWorksSteps = [
  {
    title: t('sitiWebAvvocati.howItWorks.step1.title'),
    description: t('sitiWebAvvocati.howItWorks.step1.description')
  },
  {
    title: t('sitiWebAvvocati.howItWorks.step2.title'),
    description: t('sitiWebAvvocati.howItWorks.step2.description')
  },
  {
    title: t('sitiWebAvvocati.howItWorks.step3.title'),
    description: t('sitiWebAvvocati.howItWorks.step3.description')
  },
  {
    title: t('sitiWebAvvocati.howItWorks.step4.title'),
    description: t('sitiWebAvvocati.howItWorks.step4.description')
  }
]

const useCases = [
  {
    icon: Scale,
    title: t('sitiWebAvvocati.useCases.studio.title'),
    description: t('sitiWebAvvocati.useCases.studio.description')
  },
  {
    icon: Gavel,
    title: t('sitiWebAvvocati.useCases.specialized.title'),
    description: t('sitiWebAvvocati.useCases.specialized.description')
  },
  {
    icon: Briefcase,
    title: t('sitiWebAvvocati.useCases.corporate.title'),
    description: t('sitiWebAvvocati.useCases.corporate.description')
  },
  {
    icon: Users,
    title: t('sitiWebAvvocati.useCases.associations.title'),
    description: t('sitiWebAvvocati.useCases.associations.description')
  }
]

const legalFeatures = [
  { icon: Calendar, name: t('sitiWebAvvocati.technology.legal.appointment') },
  { icon: FileText, name: t('sitiWebAvvocati.technology.legal.documents') },
  { icon: Phone, name: t('sitiWebAvvocati.technology.legal.contact') },
  { icon: BookOpen, name: t('sitiWebAvvocati.technology.legal.blog') }
]

const benefits = [
  {
    icon: TrendingUp,
    title: t('sitiWebAvvocati.benefits.benefit1.title'),
    description: t('sitiWebAvvocati.benefits.benefit1.description')
  },
  {
    icon: Users,
    title: t('sitiWebAvvocati.benefits.benefit2.title'),
    description: t('sitiWebAvvocati.benefits.benefit2.description')
  },
  {
    icon: Clock,
    title: t('sitiWebAvvocati.benefits.benefit3.title'),
    description: t('sitiWebAvvocati.benefits.benefit3.description')
  },
  {
    icon: BarChart3,
    title: t('sitiWebAvvocati.benefits.benefit4.title'),
    description: t('sitiWebAvvocati.benefits.benefit4.description')
  },
  {
    icon: Shield,
    title: t('sitiWebAvvocati.benefits.benefit5.title'),
    description: t('sitiWebAvvocati.benefits.benefit5.description')
  },
  {
    icon: Zap,
    title: t('sitiWebAvvocati.benefits.benefit6.title'),
    description: t('sitiWebAvvocati.benefits.benefit6.description')
  }
]

const faqs = [
  {
    question: t('sitiWebAvvocati.faq.q1.question'),
    answer: t('sitiWebAvvocati.faq.q1.answer')
  },
  {
    question: t('sitiWebAvvocati.faq.q2.question'),
    answer: t('sitiWebAvvocati.faq.q2.answer')
  },
  {
    question: t('sitiWebAvvocati.faq.q3.question'),
    answer: t('sitiWebAvvocati.faq.q3.answer')
  },
  {
    question: t('sitiWebAvvocati.faq.q4.question'),
    answer: t('sitiWebAvvocati.faq.q4.answer')
  },
  {
    question: t('sitiWebAvvocati.faq.q5.question'),
    answer: t('sitiWebAvvocati.faq.q5.answer')
  },
  {
    question: t('sitiWebAvvocati.faq.q6.question'),
    answer: t('sitiWebAvvocati.faq.q6.answer')
  }
]

defineOptions({
  name: 'SitiWebAvvocatiPage'
})
</script>

<style scoped>
@keyframes gradient {
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px) translateX(0px);
  }
  50% {
    transform: translateY(-20px) translateX(10px);
  }
}

@keyframes grid {
  0% {
    transform: translate(0, 0);
  }
  100% {
    transform: translate(50px, 50px);
  }
}

@keyframes bounce-subtle {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-5px);
  }
}

.animate-gradient {
  background-size: 200% 200%;
  animation: gradient 15s ease infinite;
}

.animate-float {
  animation: float 6s ease-in-out infinite;
}

.animate-float-delayed {
  animation: float 8s ease-in-out infinite;
  animation-delay: 2s;
}

.animate-grid {
  animation: grid 20s linear infinite;
}

.animate-pulse-slow {
  animation: pulse 3s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.animate-bounce-subtle {
  animation: bounce-subtle 2s ease-in-out infinite;
}
</style>

